import { IMultiplayerAdapter } from "../../../core/engine/interfaces/IMultiplayerAdapter";
import { GameController } from "../../controller/GameController";

/**
 * Adapter that connects the game logic to the multiplayer networking layer.
 * This decouples the game from React and networking implementation details.
 */
export class MultiplayerGameAdapter implements IMultiplayerAdapter {
  constructor(
    private getController: () => GameController | undefined,
    private getCanvas: () => any
  ) {}

  sendAttack(attack: { lines: number; complexity: number; type: "normal" | "clean" | "potato"; sourceConnectionId: string; holePositions: number[] }): void {
    const controller = this.getController();
    if (controller) {
      controller.sendAttack(attack);
    }
  }

  sendMoves(moves: number[], numPlayer: number): void {
    const controller = this.getController();
    if (controller) {
      controller.sendMoves(moves, numPlayer);
    }
  }

  notifyDeath(thenGone: boolean, lastAttackerNumPlayer?: number): void {
    const controller = this.getController();
    if (controller) {
      // Get lastAttackerNumPlayer from canvas if not provided
      const attackerNumPlayer = lastAttackerNumPlayer ?? this.getLastAttackerNumPlayer();
      console.log(`[MultiplayerGameAdapter.notifyDeath] Called: thenGone=${thenGone}, lastAttackerNumPlayer=${lastAttackerNumPlayer ?? "undefined"}, attackerNumPlayer=${attackerNumPlayer ?? "undefined"}`);
      controller.notifyPlayerDeath(thenGone, attackerNumPlayer);
    } else {
      console.warn(`[MultiplayerGameAdapter.notifyDeath] Controller not available`);
    }
  }

  sendStampBlock(x: number, y: number, rotation: number, blockType: number): void {
    const controller = this.getController();
    if (controller) {
      controller.sendStampBlock(x, y, rotation, blockType);
    }
  }

  getLastAttackerNumPlayer(): number | undefined {
    const canvas = this.getCanvas();
    return canvas?.lastAttackerNumPlayer;
  }

  getPlayerInfo(connectionId: string): { name: string; frags: number; deaths: number; team: number } | undefined {
    const controller = this.getController();
    if (controller) {
      const gameState = controller.getGameState();
      const playerState = gameState.connections.get(connectionId);
      if (playerState) {
        return {
          name: playerState.name,
          frags: playerState.frags || 0,
          deaths: playerState.deaths || 0,
          team: playerState.team,
        };
      }
    }
    return undefined;
  }

  getSyncpoint(): "playing" | "waitForWinner" | "waitForRestart" | "last" | undefined {
    const controller = this.getController();
    if (controller) {
      const gameState = controller.getGameState();
      return gameState.roundSyncState;
    }
    return undefined;
  }

  getGameMode(): "standard" | "survivor" | "sprint" | "hot_potato" | "team" | undefined {
    const controller = this.getController();
    if (controller) {
      const gameState = controller.getGameState();
      const mode = gameState.gameMode;
      if (
        mode === "standard" ||
        mode === "survivor" ||
        mode === "sprint" ||
        mode === "hot_potato" ||
        mode === "team"
      ) {
        return mode;
      }
    }
    return undefined;
  }

  getConnectionId(): string | undefined {
    const controller = this.getController();
    if (controller) {
      const gameState = controller.getGameState();
      return gameState.localConnectionId;
    }
    return undefined;
  }

  sendClientState(state: "playing" | "waitForWinner" | "waitForRestart" | "last"): void {
    const controller = this.getController();
    if (controller) {
      controller.sendClientState(state);
    }
  }

  sendClientFirstFrag(): void {
    const controller = this.getController();
    if (controller?.sendClientFirstFrag) {
      controller.sendClientFirstFrag();
    }
  }

  sendRespawnRequest(): void {
    const controller = this.getController();
    if (controller && controller.sendRespawnRequest) {
      controller.sendRespawnRequest();
    }
  }

  getStartLevel(): number {
    const controller = this.getController();
    if (controller) {
      const gameState = controller.getGameState();
      return gameState.params?.startLevel || 1;
    }
    return 1;
  }
}

